/**
 * Feature names used in the Copilot Scorecard for association with usage events, and as the OCV
 * `featureName` for Copilot feedback. This enum is the single source of truth for both pipelines,
 * so a feature reports the same name whether the signal is a usage event or user feedback.
 * Warning: If a new feature is added or an existing feature name is modified,
 * please update the 'copilot.cs' mapping file owned by the DIG team and notify DIGAITel@microsoft.com.
 * Reference: https://www.owiki.ms/wiki/DIG/Copilot#When_instrumenting_new_events_or_changing_feature_names_for_existing_events
 */ export var CopilotFeatureName = /*#__PURE__*/ function(CopilotFeatureName) {
    /** Represents the action to build a plan. */ CopilotFeatureName["BuildPlan"] = "Build Plan";
    /** Represents the action to create entities. */ CopilotFeatureName["CreateEntities"] = "Create Entities";
    /** Represents the action to modify entities. */ CopilotFeatureName["ModifyEntities"] = "Modify Entities";
    /** Represents the action for chat QnA. */ CopilotFeatureName["ChatQnA"] = "Chat QnA";
    /** Represents the action for task prioritization. */ CopilotFeatureName["TaskPrioritization"] = "Task Prioritization";
    /** Represents the action for Copilot chat. */ CopilotFeatureName["CopilotChat"] = "CopilotChat";
    /** Represents the action to generate a plan status report. */ CopilotFeatureName["StatusReport"] = "Status Report";
    /** Represents the action to generate a plan or Copilot tasks. */ CopilotFeatureName["PlanGeneration"] = "Plan Generation";
    /** Represents the action to assign or (re)run a task with a Copilot agent. */ CopilotFeatureName["TaskExecution"] = "Task Execution";
    return CopilotFeatureName;
}({});
/**
 * Load state for the Copilot pane lifecycle.
 */ export const CopilotLoadState = {
    /** Copilot pane not initialized or disposed */ None: "none",
    /** Copilot pane is loading */ Loading: "loading",
    /** Copilot pane fully loaded and ready */ Loaded: "loaded",
    /** Copilot pane failed to load */ Failed: "failed"
};
/**
 * Reason a Copilot load failed, used to render the appropriate error message.
 * Only meaningful when the Copilot load state is Failed.
 */ export const CopilotFailureReason = {
    /** No failure */ None: "none",
    /** Failed before/at copilot render (Catalyst SDK init, eligibility, or render) */ LoadFailed: "loadFailed",
    /** Copilot rendered but the Planner agent did not load within the timeout */ AgentLoadTimeout: "agentLoadTimeout"
};
/**
 * Maximum time in ms to wait for the Planner agent to load before removing the overlay.
 */ export const CopilotAgentLoadTimeoutMs = 25000;
/**
 * Maximum number of times to retry loading the Copilot experience after the Planner agent
 * fails to load within {@link CopilotAgentLoadTimeoutMs}. After the retries are exhausted the
 * pane transitions to the Failed state.
 */ export const CopilotAgentLoadMaxRetries = 2;
/** Default (and minimum) width in pixels of the Copilot sidecar panel */ export const CopilotSidecarPanelWidth = 400;
/** Maximum width in pixels the Copilot pane can be resized to */ export const CopilotSidecarPanelMaxWidth = 800;
/** Step size in pixels for keyboard-driven resizing of the Copilot pane (Arrow keys) */ export const CopilotPaneResizeStep = 16;
